/**
 * Декларативна нормалізація назв товарів (джерело істини в `client/src/lib/productNameNormalize/`; Node імпортує звідси ж).
 *
 * Правила вводу можна писати в будь-якому регістрі: порівняння через caseFold (uk).
 * includes / exactList / any — усі текстові збіги ігнорують регістр.
 */

/** Лаємкі лапки / маркери, щоб «MOTHER», "MOTHER" і MOTHER давали один збіг із exactList. */
const SOFT_PUNCT_FOR_MATCH = /[\u00AB\u00BB\u201C\u201D\u201E\u201F\u2033\u2036\u2039\u203A"'`´\uFEFF]/g;

/** Нормалізація для порівняння (регістр + Unicode + однакові лапки/пробіли). */
export function caseFoldForProductMatch(s) {
    let str = String(s ?? "").normalize("NFKC");
    str = str.replace(SOFT_PUNCT_FOR_MATCH, " ").replace(/\s+/g, " ").trim();
    try {
        return str.toLocaleLowerCase("uk");
    } catch {
        return str.toLowerCase();
    }
}

function matchesClause(nameFolded, c) {
    if (!c || !c.kind) return false;
    if (c.kind === "includes") return nameFolded.includes(caseFoldForProductMatch(c.pattern));
    if (c.kind === "exact") return nameFolded === caseFoldForProductMatch(c.value);
    return false;
}

export function matchesProductNameRule(nameFolded, rule) {
    if (!rule || typeof rule.result !== "string") return false;
    switch (rule.kind) {
        case "includes":
            return nameFolded.includes(caseFoldForProductMatch(rule.pattern));
        case "exactList":
            return (rule.patterns || []).some((p) => nameFolded === caseFoldForProductMatch(p));
        case "any":
            return (rule.clauses || []).some((c) => matchesClause(nameFolded, c));
        default:
            return false;
    }
}

export function applyProductNameNormalize(rawName, config) {
    if (rawName == null) return rawName;
    if (typeof rawName !== "string") return rawName;

    let name = rawName;
    if (config.trimAfterDash !== false) {
        name = name.split(" - ")[0].trim();
    }
    if (config.stripTrailingYear !== false) {
        name = name.replace(/\d{4}$/, "").trim();
    }

    const nameFolded = caseFoldForProductMatch(name);
    const rules = Array.isArray(config.rules) ? config.rules : [];
    for (const rule of rules) {
        if (matchesProductNameRule(nameFolded, rule)) {
            return rule.result;
        }
    }
    return name;
}

export function validateProductNameNormalizeConfig(config) {
    const errors = [];
    if (!config || typeof config !== "object") {
        errors.push("config має бути об'єктом");
        return { ok: false, errors };
    }
    if (!Array.isArray(config.rules)) {
        errors.push("rules має бути масивом");
        return { ok: false, errors };
    }
    config.rules.forEach((rule, i) => {
        const prefix = `rules[${i}]`;
        if (!rule || typeof rule !== "object") {
            errors.push(`${prefix}: некоректне правило`);
            return;
        }
        if (typeof rule.result !== "string" || !rule.result.trim()) {
            errors.push(`${prefix}: потрібен непорожній result`);
        }
        if (!["includes", "exactList", "any"].includes(rule.kind)) {
            errors.push(`${prefix}: невідомий kind`);
        }
        if (rule.kind === "includes") {
            if (typeof rule.pattern !== "string" || !rule.pattern.trim()) {
                errors.push(`${prefix}: для includes потрібен pattern`);
            }
        }
        if (rule.kind === "exactList") {
            if (!Array.isArray(rule.patterns) || rule.patterns.length === 0) {
                errors.push(`${prefix}: для exactList потрібний непорожній patterns`);
            }
        }
        if (rule.kind === "any") {
            if (!Array.isArray(rule.clauses) || rule.clauses.length === 0) {
                errors.push(`${prefix}: для any потрібні clauses`);
            } else {
                rule.clauses.forEach((c, j) => {
                    if (c.kind === "includes" && (!c.pattern || !String(c.pattern).trim())) {
                        errors.push(`${prefix}.clauses[${j}]: includes потребує pattern`);
                    }
                    if (c.kind === "exact" && (!c.value || !String(c.value).trim())) {
                        errors.push(`${prefix}.clauses[${j}]: exact потребує value`);
                    }
                    if (c.kind !== "includes" && c.kind !== "exact") {
                        errors.push(`${prefix}.clauses[${j}]: невідомий kind`);
                    }
                });
            }
        }
    });
    return { ok: errors.length === 0, errors };
}
